import { computed, Injectable, signal } from '@angular/core';
import { Alarm } from '../class/Alarm';

/**
 * Holds the alarms list shared between AlarmsListService/AlarmEditService (own
 * the business logic: fetching, filtering, create/update/delete) and
 * GlobalSearchManagerService/LandmarksService (read it to index/filter),
 * without either of those depending on AlarmsListService for it.
 *
 * The signal is read-only from the outside - all writes must go through
 * setAlarms/upsertAlarm(s)/removeAlarms/clear so every change to the shared
 * state is traceable to one of these methods.
 */
@Injectable({
  providedIn: 'root',
})
export class AlarmsStateService {
  private _alarms = signal<Alarm[]>([]);

  public $alarms = this._alarms.asReadonly();
  public $alarmsById = computed<Record<number, Alarm>>(() =>
    Object.fromEntries(this.$alarms().map(alarm => [alarm.uid, alarm]))
  );

  public setAlarms(alarms: Record<number, Alarm>): void {
    this._alarms.set(Object.values(alarms)?.reverse() || []);
  }

  public upsertAlarm(alarm: Alarm): void {
    this.upsertAlarms([alarm]);
  }

  public upsertAlarms(alarmsToUpsert: Alarm[]): void {
    const uidsToUpsert = new Set(alarmsToUpsert.map(alarm => alarm.uid));
    this._alarms.update(alarms => [
      ...alarms.filter(alarm => !uidsToUpsert.has(alarm.uid)),
      ...alarmsToUpsert,
    ]);
  }

  public removeAlarms(uids: number[]): void {
    const uidsToRemove = new Set(uids);
    this._alarms.update(alarms => alarms.filter(alarm => !uidsToRemove.has(alarm.uid)));
  }

  public clear(): void {
    this._alarms.set([]);
  }
}
